/**
 * Mirrors Postgres `jsonb_deep_merge` (migrations `20260807180000` +
 * `20260808090000` + `20260817120000`) — RFC 7386 JSON Merge Patch:
 *
 *   patch value is `null`     → key REMOVED
 *   patch value is an object  → recurse, replacing a non-object target with `{}`
 *                               first (RFC 7386 §2), so a nested `null` still
 *                               deletes rather than being stored literally
 *   anything else             → right-hand side wins, so a client can still
 *                               clear an array (`collapsedNodeIds: []`)
 *
 * The two implementations must agree exactly. They are the same operation
 * performed in two places — the client accumulates successive patches into the
 * session layer and `useViewPersonalization`'s pending buffer, the server
 * applies the flushed result — and a divergence is SILENT: `flush()` writes the
 * server's response into the result cache but not into `overlay` state, so a
 * mismatch only surfaces on remount. Change one, change both, and keep
 * `deepMergeJson.spec.ts` mirroring the SQL assertions.
 *
 * Deleting the last key leaves an EMPTY OBJECT rather than removing the parent:
 * `columnState: {}` is a legitimate "no entries" state and must not collapse
 * into "columnState was never set".
 *
 * Not naming which fields are nested records is the point (EAINT-781 Phase 3):
 * `levelOverrides`, `columnState`, `levelFilters` and whatever comes next all
 * merge per key without an allow-list that would have to be kept in sync.
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * `b` is deliberately NOT `Partial<T>`: a patch routinely introduces keys the
 * left-hand side has never seen (`levelFilters.lvl_b` merging into a config
 * holding only `lvl_a`) — which is the entire point of the per-key merge, and
 * something `Partial<T>` cannot express at any depth below the first.
 */
export function deepMergeJson<T extends Record<string, unknown>>(
  a: T | undefined,
  b: Record<string, unknown> | undefined
): T {
  if (a === undefined) return (b ?? {}) as T;
  if (b === undefined) return a;

  const merged: Record<string, unknown> = { ...a };
  Object.keys(b).forEach((key) => {
    const av = (a as Record<string, unknown>)[key];
    const bv = (b as Record<string, unknown>)[key];

    // RFC 7386 delete. `null` only — `undefined` is NOT a delete sentinel:
    // it is what a `Partial<…>` produces for a field the caller simply did not
    // set, and treating that as "remove it" would make every partial patch
    // destructive, which is the opposite of what the overlay is for.
    if (bv === null) {
      delete merged[key];
      return;
    }

    // RFC 7386 §2: when the target at a key is NOT an object, the target is
    // replaced by `{}` and the patch applied RECURSIVELY — it is not assigned
    // verbatim. That distinction only shows up when the patch nests a `null`
    // under an absent (or non-object) parent: assigning verbatim would STORE the
    // delete sentinel as a literal value instead of deleting. Recursing into
    // `{}` makes the nested `null` hit the delete branch above, so it can never
    // survive into the result. EAINT-801.
    // Only an object base can be merged into; anything else starts from empty
    // so the recursion still sees b's keys.
    const mergeBase = isPlainObject(av) ? av : {};
    merged[key] = isPlainObject(bv) ? deepMergeJson(mergeBase, bv) : bv;
  });
  return merged as T;
}

/**
 * Composes two merge-patches into ONE patch: `apply(compose(p, q), s)` ===
 * `apply(q, apply(p, s))`.
 *
 * That law holds EXCEPT in one place:
 *
 *  1. `q` merges an object into a key `p` replaced with a non-object (a queued
 *     `null` delete, or a scalar/array). RFC 7386 cannot express "replace, THEN
 *     merge" as one patch, so no fold can be correct there — callers must
 *     detect the crossing with `patchFoldsAcrossDelete` below and flush `p` as
 *     its own write first. The spec case `cannotFoldDeleteThenReAdd` pins the
 *     divergence rather than papering over it.
 *
 * A second, target-dependent exception used to live here: `deepMergeJson`
 * assigned the patch object verbatim when the TARGET held a non-object under a
 * key where the patch nested a `null`, so the `null` was stored rather than
 * deleting. That was a bug, not a property of the law, and EAINT-801 fixed it in
 * `deepMergeJson` + `jsonb_deep_merge`. Recorded because `patchFoldsAcrossDelete`
 * still cannot see the target, so if a target-dependent divergence is ever
 * reintroduced it will again be undetectable from the two patches alone.
 *
 * This is NOT `deepMergeJson`, and using that here is a silent data-loss bug.
 *
 * `deepMergeJson` APPLIES a patch to state, so `null` means "delete this key".
 * When both sides are patches the `null` has not been applied to anything yet —
 * it is the instruction still travelling to the server, and deleting it drops
 * the delete. Concretely (EAINT-789 SC4): a debounced accumulator holding
 * `{ levelFilters: { L1: F } }` composed with Reset's `{ levelFilters: { L1: null } }`
 * would collapse to `{ levelFilters: {} }` — a no-op on the server, so the
 * persisted filter survives locally-cleared state and resurrects on remount.
 *
 * So: recurse on two plain objects, otherwise the right-hand side wins —
 * INCLUDING `null`, which is retained verbatim as the delete instruction.
 * Only `undefined` is skipped, matching `deepMergeJson`'s reading of it as
 * "the caller did not set this field" rather than a sentinel.
 */
export function composeJsonPatch<T extends Record<string, unknown>>(
  a: T | undefined,
  b: Record<string, unknown> | undefined
): T {
  if (a === undefined) return (b ?? {}) as T;
  if (b === undefined) return a;

  const composed: Record<string, unknown> = { ...a };
  Object.keys(b).forEach((key) => {
    const av = (a as Record<string, unknown>)[key];
    const bv = (b as Record<string, unknown>)[key];

    if (bv === undefined) return;

    composed[key] = isPlainObject(av) && isPlainObject(bv) ? composeJsonPatch(av, bv) : bv;
  });
  return composed as T;
}

/**
 * True when composing `next` onto `pending` would cross a queued delete with a
 * re-add — `pending` holds `null` at a key where `next` holds a plain object.
 *
 * RFC 7386 cannot express "delete this key, THEN set it to this object" as a
 * single patch, and that is not a gap in `composeJsonPatch`: a merge patch
 * describes a merge, so `{k: {a: 1}}` applied to `{k: {a: 0, b: 2}}` must yield
 * `{k: {a: 1, b: 2}}`, while delete-then-set yields `{k: {a: 1}}`. Folding the
 * two would silently resurrect `b` — server-side only, since the local overlay
 * applies the delete correctly, so the divergence stays invisible until remount
 * (the exact failure mode this file's header warns about).
 *
 * Reachable via Reset (`useLevelFilters.reset()` emits a per-level `null`)
 * followed by a re-filter inside the 800 ms autosave window. The caller's
 * remedy is to flush the pending patch as its own write before composing, so
 * the delete and the re-add land as two ordered PUTs instead of one wrong one.
 *
 * SCOPE: this answers "can these two PATCHES fold", and nothing more. It is not
 * a general "is the fold safe" predicate — it never sees the TARGET state, so
 * any target-dependent divergence is invisible to it and a `false` here does not
 * certify the write. (The one such divergence that existed, EAINT-801, is now
 * fixed in `deepMergeJson` itself rather than worked around here.)
 */
export function patchFoldsAcrossDelete(
  pending: Record<string, unknown> | undefined,
  next: Record<string, unknown> | undefined
): boolean {
  if (!pending || !next) return false;

  return Object.keys(next).some((key) => {
    const pv = pending[key];
    const nv = next[key];
    // `null` is the case that motivated this, but it is not the only one. The
    // general condition is "pending REPLACES the key with a non-object, next
    // then MERGES an object into it": composing takes the right-hand side
    // wholesale, so the fold merges into the original state where the
    // sequential apply merges into the replacement. A scalar or an array on
    // the left diverges exactly as `null` does — `{k:5}` then `{k:{a:1}}`
    // against `{k:{a:0,b:2}}` folds to `{a:1,b:2}` but applies to `{a:1}`.
    // `undefined` is excluded: the key is absent from `pending`, so both
    // orders merge `nv` into the same state.
    if (pv !== undefined && !isPlainObject(pv)) return isPlainObject(nv);
    if (isPlainObject(pv) && isPlainObject(nv)) return patchFoldsAcrossDelete(pv, nv);
    return false;
  });
}
